import React, { useState } from 'react'
import { css } from '@emotion/core'
import { colors, H1, Paragraph, Row, useOverlay, WhiteTheme } from '@fs/zion-ui'
import { ContentCompose } from '@fs/zion-icon/dist/cjs/icons'
import { useTranslation } from 'react-i18next'
import CaptionOverlay from '../CaptionOverlay/CaptionOverlay'

import { useMemoryBook } from '../../../../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../../../../helpers/getHasDarkBackground'

const pencilBarCss = (isEmpty, isHeading, hasDarkBackground) => {
  let background
  if (isEmpty) {
    background = hasDarkBackground ? colors.gray80 : colors.gray03
  } else {
    background = 'transparent'
  }
  return css`
    display: flex;
    justify-content: start;
    align-items: center;
    margin-top: 8px;
    padding: ${isHeading ? '12px' : '6px'};
    background: ${background};
    width: 100%;
    border: none;
    position: relative;

    &:hover {
      transition: 0.3s ease-in-out;
      background-color: ${hasDarkBackground ? colors.gray80 : colors.gray03}; /* Subtle background change on hover */
    }

    &:focus {
      outline: 3px solid black;
      outline-offset: 2px;
    }

    h1 {
      margin-inline-start: 0; /* Reset the negative margin for headings to prevent wrapping early */
    }

    @media print {
      background: transparent;
    }
  `
}

export default function Caption({
  caption = '',
  title = '',
  pageNumber,
  positionIndex,
  isHeading = false,
  isPrintView = false,
}) {
  const [t] = useTranslation()
  // Only track draft state while overlay is open - use props as source of truth
  const [draftCaptionState, setDraftCaptionState] = useState('')
  const [draftTitleState, setDraftTitleState] = useState('')
  const pencilOverlay = useOverlay()

  const { memoryBook } = useMemoryBook()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  const textFieldOnChange = (e) => {
    setDraftCaptionState(e.target.value)
  }

  const titleFieldOnChange = (e) => {
    setDraftTitleState(e.target.value)
  }

  // Reset draft state to current props when opening overlay
  const handleOpen = (e) => {
    setDraftCaptionState(caption)
    setDraftTitleState(title)
    pencilOverlay.open(e)
  }

  const isCaptionEmpty = caption.length === 0
  const isTitleEmpty = title.length === 0
  const isEmpty = isHeading ? isTitleEmpty : isCaptionEmpty

  if (isPrintView && isEmpty) {
    return null
  }

  let titleText = ''
  if (isHeading) {
    if (isTitleEmpty) {
      titleText = t('Caption.addTitle.label', 'Add a Title')
    } else {
      titleText = t('Caption.editTitle.label', 'Edit a Title')
    }
  } else if (isCaptionEmpty) {
    titleText = t('Caption.addCaption.label', 'Add a Caption')
  } else {
    titleText = t('Caption.editCaption.label', 'Edit a Caption')
  }

  const placeholderCaptionText = caption || t('TextPicker.addText.label', 'Add Text')
  const placeholderTitleText = title || t('TextPicker.addText.label', 'Add Text')

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        css={pencilBarCss(isEmpty, isHeading, hasDarkBackground)}
        aria-label={titleText}
      >
        <Row gutters="xs" alignY="middle">
          {!isPrintView && isEmpty && (
            <div>
              <ContentCompose color={colors.gray40} size="xs" />
            </div>
          )}
          {hasDarkBackground ? (
            <WhiteTheme>
              {isHeading && <H1 size="H4">{placeholderTitleText}</H1>}
              {!isHeading && <Paragraph size="xs">{placeholderCaptionText}</Paragraph>}
            </WhiteTheme>
          ) : (
            <>
              {isHeading && <H1 size="H4">{placeholderTitleText}</H1>}
              {!isHeading && <Paragraph size="xs">{placeholderCaptionText}</Paragraph>}
            </>
          )}
        </Row>
      </button>

      {/* PencilOverlay */}
      {(pencilOverlay.isOpen || pencilOverlay.transitioning) && (
        <CaptionOverlay
          overlay={pencilOverlay}
          captionState={draftCaptionState}
          textFieldOnChange={textFieldOnChange}
          titleState={draftTitleState}
          titleFieldOnChange={titleFieldOnChange}
          isHeading={isHeading}
          positionIndex={positionIndex}
          pageNumber={pageNumber}
          titleText={titleText}
        />
      )}
    </>
  )
}
